import { useState, useEffect } from "react";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { fetchRolePageAccess, updateRolePageAccess } from "../../services/api";
import { DEFAULT_PAGE_ROLES, MANAGEABLE_PAGES } from "./constants";

export default function RolePageAccessSection() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchRolePageAccess()
      .then((data) => {
        setConfig(data.pages || {});
        setLastUpdated(
          data.updatedAt ? { at: data.updatedAt, by: data.updatedBy } : null,
        );
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const getRoles = (path) => {
    if (config[path]) return config[path].roles;
    return DEFAULT_PAGE_ROLES[path] || ["admin"];
  };

  const toggleRole = (path, role) => {
    if (role === "admin") return;
    const current = getRoles(path);
    const updated = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    if (!updated.includes("admin")) updated.push("admin");
    setConfig({ ...config, [path]: { roles: updated } });
    setSuccess(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const result = await updateRolePageAccess(config);
      setConfig(result.pages);
      setLastUpdated({ at: result.updatedAt, by: result.updatedBy });
      setSuccess("Page access configuration saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({});
    setSuccess(null);
  };

  const ROLES = [
    { id: "sales", label: "Sales" },
    { id: "exec", label: "Exec" },
    { id: "admin", label: "Admin" },
  ];

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
        marginTop: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div>
          <h3
            style={{
              fontSize: 15,
              fontWeight: 700,
              fontFamily: fonts.heading,
              color: colors.text,
              margin: 0,
            }}
          >
            Page Access by Role
          </h3>
          <p
            style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}
          >
            Control which pages each role can see in the sidebar.
          </p>
        </div>
        {lastUpdated && (
          <span style={{ fontSize: 11, color: colors.textSecondary }}>
            Last updated by {lastUpdated.by?.split("@")[0]} ·{" "}
            {new Date(lastUpdated.at).toLocaleDateString()}
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            background: "rgba(239,68,68,0.08)",
            color: colors.danger,
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            background: "rgba(16,185,129,0.08)",
            color: colors.success,
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <Loader2
            size={20}
            color={colors.accent}
            className="spin"
          />
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 70px 70px 70px",
              gap: 0,
              borderBottom: `2px solid ${colors.border}`,
              paddingBottom: 8,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: colors.textSecondary,
              }}
            >
              Page
            </div>
            {ROLES.map((r) => (
              <div
                key={r.id}
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.textSecondary,
                  textAlign: "center",
                }}
              >
                {r.label}
              </div>
            ))}
          </div>

          {MANAGEABLE_PAGES.map((page) => {
            const roles = getRoles(page.path);
            const isDefault = !config[page.path];
            return (
              <div
                key={page.path}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 70px 70px 70px",
                  gap: 0,
                  padding: "7px 0",
                  borderBottom: `1px solid ${colors.borderLight}`,
                  opacity: isDefault ? 0.75 : 1,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: colors.text,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {page.label}
                  {isDefault && (
                    <span
                      style={{
                        fontSize: 10,
                        color: colors.textSecondary,
                        fontStyle: "italic",
                      }}
                    >
                      default
                    </span>
                  )}
                </div>
                {ROLES.map((r) => (
                  <div key={r.id} style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={roles.includes(r.id)}
                      disabled={r.id === "admin"}
                      onChange={() => toggleRole(page.path, r.id)}
                      style={{
                        cursor: r.id === "admin" ? "not-allowed" : "pointer",
                        accentColor: colors.accent,
                      }}
                    />
                  </div>
                ))}
              </div>
            );
          })}

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: fonts.body,
                background: colors.accent,
                color: colors.white,
                border: "none",
                borderRadius: 6,
                cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? (
                <Loader2
                  size={14}
                  className="spin"
                />
              ) : (
                <Save size={14} />
              )}
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleReset}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                fontSize: 13,
                fontFamily: fonts.body,
                background: "none",
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              <RotateCcw size={14} />
              Reset to Defaults
            </button>
          </div>
        </>
      )}
    </div>
  );
}
